import { Input, InputProps } from "@chakra-ui/react";
import { useState } from "react";

// This component allows users to input numbers, including decimals and negative values.
// It handles different locales by allowing both commas and dots as decimal separators.
// The component ensures that only valid number formats are accepted and calls the onChange
// handler only when a complete valid number is entered.

interface DecimalInputProps extends Omit<InputProps, "onChange" | "value"> {
  value: number;
  onChange?: (value: number) => void;
}

const isValidNumberInput = (value: string): boolean => {
  // Allow empty string, numbers, decimals in progress, negative numbers
  return /^-?(\d+\.?\d*|\.\d+|\.)?$/.test(value);
};

const isIncompleteNumber = (value: string) => {
  return value === "" || value === "-" || value.endsWith(".");
};

export const DecimalInput = ({ onChange, value, ...rest }: DecimalInputProps) => {
  const [stringValue, setStringValue] = useState<string>(String(value));

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Replace comma with dot for decimal separator (handles different locales)
    let inputValue = event.target.value;
    inputValue = inputValue.replace(",", ".");
    if (inputValue.startsWith(".")) inputValue = "0" + inputValue;

    // Always allow valid number input patterns
    if (isValidNumberInput(inputValue)) {
      setStringValue(inputValue);

      const numValue = parseFloat(inputValue);

      onChange?.(!isNaN(numValue) ? numValue : 0);
    }
  };

  const displayValue = isIncompleteNumber(stringValue) ? stringValue : String(value);

  return <Input {...rest} value={displayValue} onChange={handleChange} />;
};
